import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useBot } from '../../context/BotContext';

const { FiStar, FiSearch, FiLoader, FiTrash2, FiExternalLink, FiTrendingUp, FiTrendingDown, FiRefreshCw, FiBell } = FiIcons;

export default function WatchlistView() {
  const { watchlist, addTokenToWatchlist, removeTokenFromWatchlist, refreshPrices, isRefreshing, notify } = useBot();
  const [searchInput, setSearchInput] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  const handleSearchAndAdd = async () => {
    if (!searchInput.trim()) return;
    
    setIsSearching(true);
    try {
      const res = await fetch(`https://api.dexscreener.com/latest/dex/tokens/${searchInput.trim()}`);
      const data = await res.json();
      
      const solPairs = data.pairs?.filter(p => {
        const quoteSymbol = p.quoteToken?.symbol?.toUpperCase();
        return p.chainId === 'solana' && p.priceNative && (quoteSymbol === 'SOL' || quoteSymbol === 'WSOL');
      });

      if (solPairs && solPairs.length > 0) {
        solPairs.sort((a, b) => (b.liquidity?.usd || 0) - (a.liquidity?.usd || 0));
        const pair = solPairs[0];

        addTokenToWatchlist(pair.baseToken.symbol, pair.baseToken.address, pair.priceNative, pair.priceUsd || null);
        setSearchInput('');
      } else {
        notify('Token not found or lacks a SOL liquidity pair', 'error');
      }
    } catch (error) {
      notify('Failed to fetch token data', 'error');
    } finally {
      setIsSearching(false);
    }
  };

  const formatPrice = (price) => {
    const p = parseFloat(price);
    if (!p) return '0';
    return p.toLocaleString('en-US', { maximumSignificantDigits: 6 });
  };

  return (
    <div className="p-4 relative min-h-[calc(100vh-140px)] lg:min-h-[calc(100vh-40px)]">
      
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-2">
          <SafeIcon icon={FiStar} className="text-yellow-400 text-xl" />
          <h2 className="text-white font-bold text-xl">Watchlist</h2>
        </div>
        <button 
          onClick={refreshPrices} 
          disabled={isRefreshing}
          className="bg-[#2c2c2e] p-2 rounded-full text-gray-400 hover:text-white disabled:opacity-50 transition-colors border border-[#3c3c3e]"
        >
          <SafeIcon icon={FiRefreshCw} className={isRefreshing ? 'animate-spin text-blue-400' : ''} />
        </button>
      </div>

      {/* Add Token Section */}
      <div className="bg-[#1c1c1d] p-4 rounded-xl border border-[#2c2c2e] shadow-lg mb-6">
        <div className="relative flex gap-2">
          <input 
            type="text" 
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearchAndAdd()}
            placeholder="Paste token address to watch..." 
            className="flex-1 bg-[#0a0a0b] border border-[#3c3c3e] rounded-lg py-2.5 pl-3 pr-3 text-white text-sm font-mono focus:outline-none focus:border-blue-500 transition-colors"
          />
          <button 
            onClick={handleSearchAndAdd}
            disabled={isSearching || !searchInput.trim()}
            className="bg-blue-600 hover:bg-blue-500 disabled:bg-[#2c2c2e] disabled:text-gray-500 text-white px-4 rounded-lg text-sm font-medium transition-colors flex items-center justify-center min-w-[80px]"
          >
            {isSearching ? <SafeIcon icon={FiLoader} className="animate-spin" /> : 'Add'}
          </button>
        </div>
      </div>

      {/* Watchlist Grid */}
      {watchlist.length === 0 ? (
        <div className="text-center text-gray-500 mt-16">
          <SafeIcon icon={FiSearch} className="text-4xl mx-auto mb-2 opacity-50" />
          <p>Your watchlist is empty.</p>
          <p className="text-xs mt-1">Paste an address above to track live prices.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <AnimatePresence>
            {watchlist.map((item) => {
              const currentPrice = item.currentPrice || item.priceNative;
              const initialPrice = item.initialPrice || item.priceNative;
              
              const pnlSinceAdd = initialPrice > 0 ? ((currentPrice - initialPrice) / initialPrice) * 100 : 0;
              const isTrackedPositive = pnlSinceAdd >= 0;
              const isPositive24h = item.priceChange24h >= 0;
              
              const totalMilestones = (item.milestonesHitUp?.length || 0) + (item.milestonesHitDown?.length || 0);

              return (
                <motion.div 
                  key={item.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="bg-[#1c1c1d] border border-[#2c2c2e] rounded-xl p-4 relative group"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1 pr-4">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-white font-bold text-lg">${item.symbol}</h3>
                        <a 
                          href={`https://dexscreener.com/solana/${item.address}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-gray-500 hover:text-blue-400 transition-colors flex items-center gap-1 text-[10px] bg-[#2c2c2e] px-1.5 py-0.5 rounded border border-[#3c3c3e]"
                        >
                          Chart <SafeIcon icon={FiExternalLink} className="text-[10px]" />
                        </a>
                        {totalMilestones > 0 && (
                          <span className="text-[10px] bg-blue-500/10 text-blue-400 px-1.5 py-0.5 rounded border border-blue-500/20 flex items-center gap-1">
                            <SafeIcon icon={FiBell} /> {totalMilestones} Alerts
                          </span>
                        )}
                      </div>
                      <p className="text-gray-500 text-[10px] font-mono truncate max-w-[200px]">
                        {item.address}
                      </p>
                    </div>
                    
                    <div className="text-right">
                      <div className="text-white font-mono font-bold text-md">
                        {item.currentPriceUsd
                          ? <>${formatPrice(item.currentPriceUsd)}</>
                          : <>{formatPrice(currentPrice)} <span className="text-[10px] text-gray-500">SOL</span></>
                        }
                      </div>
                      <div className="flex flex-col items-end gap-1 mt-2">
                        <div className={`flex items-center gap-1 text-[11px] font-medium ${isPositive24h ? 'text-green-400' : 'text-red-400'}`}>
                          <span className="text-gray-500 text-[9px] uppercase tracking-wider">24H</span>
                          <SafeIcon icon={isPositive24h ? FiTrendingUp : FiTrendingDown} className="text-[10px]" />
                          {isPositive24h ? '+' : ''}{item.priceChange24h.toFixed(2)}%
                        </div>
                        <div className={`flex items-center gap-1 text-[11px] font-medium ${isTrackedPositive ? 'text-green-400' : 'text-red-400'}`}>
                          <span className="text-gray-500 text-[9px] uppercase tracking-wider">Since Add</span>
                          <SafeIcon icon={isTrackedPositive ? FiTrendingUp : FiTrendingDown} className="text-[10px]" />
                          {isTrackedPositive ? '+' : ''}{pnlSinceAdd.toFixed(2)}%
                        </div>
                      </div>
                    </div>
                  </div>

                  <button 
                    onClick={() => removeTokenFromWatchlist(item.id)}
                    className="absolute top-1/2 -translate-y-1/2 right-4 w-8 h-8 bg-red-500/10 text-red-400 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity md:hover:bg-red-500/20"
                  >
                    <SafeIcon icon={FiTrash2} />
                  </button>
                  
                  {/* Mobile visible delete button */}
                  <button 
                    onClick={() => removeTokenFromWatchlist(item.id)}
                    className="absolute bottom-2 right-2 text-gray-600 hover:text-red-400 p-2 md:hidden"
                  >
                    <SafeIcon icon={FiTrash2} className="text-sm" />
                  </button>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}